import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import {
  SUPPORT_TICKET_CATEGORIES,
  SUPPORT_TICKET_CATEGORY_LABELS,
  SUPPORT_TICKET_STATUSES,
  type AdminSupportListResponse,
  type AdminSupportTicket,
  type SupportTicketStatus,
} from '@nexus/shared';

import { ApiError, apiGet } from '../../lib/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  CellNote,
  DataTable,
  EmptyState,
  LoadingPage,
  PageHeader,
  Pill,
  Pills,
  SearchField,
  SelectField,
  Stat,
  StatGrid,
  When,
  statusLabel,
  statusTone,
  type Column,
} from '../../ui';

/**
 * The support queue: what needs a reply first, then everything else.
 *
 * The filters are in the URL — a status pill, a category, a search box — so
 * a reload keeps them and a link to "the open referral tickets" is a link.
 * The search reaches subjects, message bodies and the creator's email, which
 * is what an operator holding an email in one hand actually types.
 */
export function AdminSupport(): React.JSX.Element {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<AdminSupportListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [text, setText] = useState(params.get('q') ?? '');
  const typed = useRef(false);

  const status = params.get('status') ?? '';
  const category = params.get('category') ?? '';
  const page = Number(params.get('page') ?? '1') || 1;
  const q = params.get('q') ?? '';

  const load = useCallback(async () => {
    const query = new URLSearchParams();
    if (status) query.set('status', status);
    if (category) query.set('category', category);
    if (q) query.set('q', q);
    query.set('page', String(page));
    try {
      setData(await apiGet<AdminSupportListResponse>(`/admin/support?${query.toString()}`));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the queue.');
    }
  }, [status, category, q, page]);

  useEffect(() => {
    void load();
  }, [load]);

  /* The search is debounced into the URL; the URL is what loads. */
  useEffect(() => {
    if (!typed.current) return;
    const handle = setTimeout(() => {
      setParam('q', text.trim());
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  function setParam(key: string, value: string): void {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setParams(next);
  }

  if (data === null) {
    if (error) {
      return (
        <>
          <PageHeader eyebrow="Operator" title="Support" />
          <Alert tone="critical" title="Could not load the queue.">
            <span>{error}</span>
          </Alert>
        </>
      );
    }
    return <LoadingPage eyebrow="Operator" title="Support" stats />;
  }

  const pages = Math.max(1, Math.ceil(data.total / data.pageSize));

  return (
    <>
      <PageHeader
        eyebrow="Operator"
        title="Support"
        lead="Every ticket a creator has opened. Open ones need a reply from you; the rest are waiting on them, or done."
      />

      {error && <Alert tone="critical">{error}</Alert>}

      <StatGrid>
        <Stat label="Needs a reply" value={data.counts.OPEN} emphasis={data.counts.OPEN > 0} note="Open tickets." />
        <Stat label="Unread" value={data.counts.unread} note="Live tickets with a creator message you have not opened." />
        <Stat label="Waiting on creators" value={data.counts.WAITING_ON_CREATOR} />
        <Stat label="Resolved" value={data.counts.RESOLVED} note={`${data.counts.CLOSED} closed.`} />
      </StatGrid>

      <Card label="Find a ticket">
        <Pills label="Status">
          <Pill selected={status === ''} onClick={() => setParam('status', '')}>
            All
          </Pill>
          {SUPPORT_TICKET_STATUSES.map((s) => (
            <Pill key={s} selected={status === s} count={data.counts[s]} onClick={() => setParam('status', s)}>
              {statusLabel(s, 'support')}
            </Pill>
          ))}
        </Pills>
        <div className="field-row">
          <SelectField label="Topic" name="category" value={category} onChange={(e) => setParam('category', e.target.value)}>
            <option value="">Every topic</option>
            {SUPPORT_TICKET_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {SUPPORT_TICKET_CATEGORY_LABELS[c]}
              </option>
            ))}
          </SelectField>
          <SearchField
            label="Search"
            value={text}
            onChange={(e) => {
              typed.current = true;
              setText(e.target.value);
            }}
            placeholder="Subject, message, or the creator's email"
          />
        </div>
      </Card>

      <Card title={`Tickets (${data.total})`}>
        <TicketTable tickets={data.tickets} />
        {pages > 1 && (
          <div className="actions">
            <Button disabled={page <= 1} onClick={() => setParam('page', String(page - 1))}>
              Previous
            </Button>
            <span className="muted">
              Page {page} of {pages}
            </span>
            <Button disabled={page >= pages} onClick={() => setParam('page', String(page + 1))}>
              Next
            </Button>
          </div>
        )}
      </Card>
    </>
  );
}

function TicketTable({ tickets }: { tickets: AdminSupportTicket[] }): React.JSX.Element {
  const columns: Array<Column<AdminSupportTicket>> = [
    {
      key: 'subject',
      header: 'Ticket',
      primary: true,
      cell: (t) => (
        <>
          <Link to={`/admin/support/${t.id}`}>{t.subject}</Link>
          <CellNote>{t.preview}</CellNote>
        </>
      ),
    },
    {
      key: 'creator',
      header: 'Creator',
      cell: (t) => (
        <>
          {t.creator.displayName ?? t.creator.email}
          <CellNote>{t.creator.email}</CellNote>
        </>
      ),
    },
    { key: 'category', header: 'Topic', cell: (t) => SUPPORT_TICKET_CATEGORY_LABELS[t.category] },
    {
      key: 'status',
      header: 'Status',
      cell: (t) => (
        <>
          <Badge tone={statusTone(t.status, 'support')}>{statusLabel(t.status as SupportTicketStatus, 'support')}</Badge>
          {t.unread > 0 && <CellNote>{t.unread} unread</CellNote>}
        </>
      ),
    },
    { key: 'assigned', header: 'Assigned', cell: (t) => t.assignedToEmail ?? <span className="muted">—</span> },
    { key: 'last', header: 'Last activity', cell: (t) => <When value={t.lastMessageAt} withTime /> },
  ];

  return (
    <DataTable
      columns={columns}
      rows={tickets}
      getKey={(t) => t.id}
      isHighlighted={(t) => t.status === 'OPEN'}
      empty={<EmptyState title="No tickets match.">Nobody is waiting on you here.</EmptyState>}
    />
  );
}
